import { useEffect, useState } from 'react';
import {
  BOT_TIERS,
  BOT_TIER_HINTS,
  BOT_TIER_NAMES,
  MAP_ROTATION,
  getMap,
} from '@game/shared';
import { cn } from '../lib/cn.js';
import { useGameStore, type HubGroupView } from '../game/store.js';
import { TOUCH_MODE } from '../game/touch.js';

const ARENA_NAMES = MAP_ROTATION.map((id) => getMap(id).name);
const TIER_NAMES = BOT_TIERS.map((tier) => BOT_TIER_NAMES[tier]);
const TIER_HINTS = BOT_TIERS.map((tier) => BOT_TIER_HINTS[tier]);

/** Re-renders ~5×/s so the pad countdown ticks; deadline lives in the store. */
const useCountdown = (endsAtMs: number): number => {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (endsAtMs === 0) {
      setRemaining(0);
      return;
    }
    const update = () => setRemaining(Math.max(0, endsAtMs - performance.now()));
    update();
    const timer = window.setInterval(update, 200);
    return () => window.clearInterval(timer);
  }, [endsAtMs]);
  return remaining;
};

const GroupCard = ({ group }: { group: HubGroupView }) => {
  const remainingMs = useCountdown(group.countdownEndsAtMs);
  const counts = group.teams.map((team) => team.length);
  return (
    <div
      className={cn(
        'rounded-lg border px-3.5 py-2.5',
        group.mine
          ? 'border-team-blue/60 bg-team-blue/10'
          : 'border-white/10 bg-surface-900/70',
      )}
    >
      <div className="flex items-baseline justify-between gap-4">
        <span className="font-bold tracking-wide">{group.id}</span>
        {group.countdownEndsAtMs > 0 ? (
          <span className="tabular text-lg font-bold text-accent">
            {Math.ceil(remainingMs / 1000)}
          </span>
        ) : (
          <span className="tabular text-xs text-ink-muted">
            {group.solo
              ? `${counts[0]}/${group.capacity}`
              : `${counts[0]}/${group.capacity} · ${counts[1]}/${group.capacity}`}
          </span>
        )}
      </div>
      <div className="mt-1 flex gap-3 text-xs">
        <span className="text-team-blue">
          {group.teams[0].map((row) => row.name).join(', ') || '—'}
        </span>
        <span className="text-ink-muted">vs</span>
        {group.solo ? (
          <span className="text-team-red">
            {Math.max(1, group.teams[0].length) * group.botsPerPlayer} bots
          </span>
        ) : (
          <span className="text-team-red">
            {group.teams[1].map((row) => row.name).join(', ') || '—'}
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Everything the walkable lobby needs to say, without ever taking the mouse:
 * the pointer stays locked for walking, so pad interactions are keyboard-driven
 * and this overlay is purely informational (pointer-events-none). On touch
 * there are no keys and no locked pointer, so the vote rows and the ready
 * offer become tappable — the only interactive parts of the overlay.
 */
export const HubOverlay = ({
  onVote,
  onVoteDifficulty,
  onReady,
}: {
  onVote: (mapIndex: number) => void;
  onVoteDifficulty: (tier: number) => void;
  onReady: () => void;
}) => {
  const groups = useGameStore((state) => state.hubGroups);
  const mine = groups.find((group) => group.mine) ?? null;
  const countdownMs = useCountdown(mine?.countdownEndsAtMs ?? 0);

  const myRow = mine?.teams.flat().find((row) => row.isSelf);
  const myTeamIndex = mine ? (mine.teams[0].some((row) => row.isSelf) ? 0 : 1) : 0;
  const myTeam = mine?.teams[myTeamIndex];
  const otherTeam = mine?.teams[myTeamIndex === 0 ? 1 : 0];
  // PvP: an under-filled side with someone to fight can offer to start short.
  // Vs-bot: any under-filled room can start early — the bots scale to match.
  const shorthanded =
    mine !== null &&
    myTeam !== undefined &&
    myTeam.length < mine.capacity &&
    (mine.solo || (otherTeam !== undefined && otherTeam.length > 0));

  const totalVotes = mine?.voteCounts.reduce((sum, count) => sum + count, 0) ?? 0;
  const totalTierVotes =
    mine?.difficultyCounts.reduce((sum, count) => sum + count, 0) ?? 0;

  return (
    <div className="pointer-events-none fixed inset-0">
      <div className="absolute left-1/2 top-6 -translate-x-1/2 rounded-full bg-surface-900/80 px-5 py-2 text-sm text-ink-muted backdrop-blur">
        {mine
          ? mine.countdownEndsAtMs > 0
            ? 'Match found — get ready'
            : 'Queued — waiting for opponents'
          : 'Walk onto a pad to queue · versus pads in back · vs-bot rooms up front'}
      </div>

      <div className="absolute right-6 top-16 flex w-56 flex-col gap-2">
        {groups.map((group) => (
          <GroupCard key={group.id} group={group} />
        ))}
      </div>

      {mine && mine.countdownEndsAtMs > 0 && (
        <div className="absolute left-1/2 top-[30%] -translate-x-1/2 text-center">
          <div className="text-7xl font-extrabold tabular [text-shadow:0_4px_16px_rgba(0,0,0,0.6)]">
            {Math.ceil(countdownMs / 1000)}
          </div>
        </div>
      )}

      {mine && (
        <div className="absolute bottom-8 left-1/2 w-[min(460px,92vw)] -translate-x-1/2 rounded-xl border border-white/10 bg-surface-900/85 p-5 backdrop-blur">
          <div className="text-[11px] uppercase tracking-[0.25em] text-ink-muted">
            Vote for the arena
          </div>
          <div className="mt-2.5 space-y-1.5">
            {ARENA_NAMES.map((name, index) => {
              const votes = mine.voteCounts[index] ?? 0;
              const share = totalVotes > 0 ? votes / totalVotes : 0;
              const chosen = mine.myVote === index;
              return (
                <button
                  key={name}
                  className={cn(
                    'flex w-full items-center gap-3 text-left',
                    TOUCH_MODE && 'pointer-events-auto rounded px-1 py-0.5 active:bg-white/10',
                  )}
                  onClick={() => TOUCH_MODE && onVote(index)}
                >
                  {!TOUCH_MODE && (
                    <kbd className="inline-block w-6 rounded border border-white/15 bg-white/10 py-px text-center text-[11px]">
                      {index + 1}
                    </kbd>
                  )}
                  <span className={cn('w-24 text-sm', chosen && 'font-bold text-team-blue')}>
                    {name}
                  </span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full bg-team-blue transition-[width] duration-300"
                      style={{ width: `${share * 100}%` }}
                    />
                  </div>
                  <span className="tabular w-8 text-right text-xs text-ink-muted">
                    {votes > 0
                      ? `${Math.round(share * 100)}%`
                      : ''}
                  </span>
                </button>
              );
            })}
          </div>
          {mine.solo && (
            <>
              <div className="mt-4 text-[11px] uppercase tracking-[0.25em] text-ink-muted">
                Vote bot difficulty
              </div>
              <div className="mt-2.5 space-y-1.5">
                {TIER_NAMES.map((name, index) => {
                  const votes = mine.difficultyCounts[index] ?? 0;
                  const share = totalTierVotes > 0 ? votes / totalTierVotes : 0;
                  const chosen = mine.myDifficultyVote === index;
                  return (
                    <button
                      key={name}
                      className={cn(
                        'flex w-full items-center gap-3 text-left',
                        TOUCH_MODE &&
                          'pointer-events-auto rounded px-1 py-0.5 active:bg-white/10',
                      )}
                      onClick={() => TOUCH_MODE && onVoteDifficulty(index)}
                    >
                      {!TOUCH_MODE && (
                        <kbd className="inline-block w-6 rounded border border-white/15 bg-white/10 py-px text-center text-[11px]">
                          {index + 5}
                        </kbd>
                      )}
                      <span
                        className={cn('w-24 text-sm', chosen && 'font-bold text-team-red')}
                        title={TIER_HINTS[index]}
                      >
                        {name}
                      </span>
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                        <div
                          className="h-full bg-team-red transition-[width] duration-300"
                          style={{ width: `${share * 100}%` }}
                        />
                      </div>
                      <span className="tabular w-8 text-right text-xs text-ink-muted">
                        {votes > 0 ? `${Math.round(share * 100)}%` : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          <p className="mt-3 text-xs text-ink-muted">
            {totalVotes > 0 || totalTierVotes > 0
              ? 'Every vote is a ticket in the draw — more votes, better odds.'
              : mine.solo
                ? 'No votes yet — arena and difficulty will be picked at random.'
                : 'No votes yet — the arena will be picked at random.'}
            {shorthanded &&
              !TOUCH_MODE &&
              (myRow?.ready
                ? mine.solo
                  ? ' · Ready — the room starts with whoever is here.'
                  : ' · Ready — you will fight outnumbered.'
                : mine.solo
                  ? ' · Press F to start now — the bots scale to your headcount.'
                  : ' · Press F to start outnumbered.')}
          </p>
          {shorthanded && TOUCH_MODE && (
            <button
              className={cn(
                'pointer-events-auto mt-2 w-full rounded-lg border py-2 text-sm font-bold',
                myRow?.ready
                  ? 'border-team-blue/60 bg-team-blue/25 text-team-blue'
                  : 'border-white/15 bg-white/10 active:bg-white/20',
              )}
              onClick={onReady}
            >
              {myRow?.ready
                ? mine.solo
                  ? 'Ready — starting with whoever is here'
                  : 'Ready — fighting outnumbered'
                : mine.solo
                  ? 'Start now'
                  : 'Fight outnumbered'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
